<script setup>

import MultiPreJob from "@/components/bookings/MultiPreJob"
import PreJobAirBooking from "@/classes/viewmodels/PreJobAirBooking";
import PreJobSeaBooking from "@/classes/viewmodels/PreJobSeaBooking";
import PreJobRoadBooking from "@/classes/viewmodels/PreJobRoadBooking";
import PreJobRailBooking from "@/classes/viewmodels/PreJobRailBooking";
import API from "@/classes/global/API";
import PreJobAirBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";
import PreJobSeaBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";
import PreJobRoadBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";
import PreJobRailBookingValidation from "@/classes/bookings/PreJobRoadBookingValidation";

import {inject, ref, computed, onMounted} from 'vue';

import {useRoute} from "vue-router";
import {useRouter} from 'vue-router';

const validationViewModel = inject("validationViewModel");
const preJobAirBooking = inject("preJobAirBooking");
const preJobSeaBooking = inject("preJobSeaBooking");
const preJobRoadBooking = inject("preJobRoadBooking");
const preJobRailBooking = inject("preJobRailBooking");

let editPreJobAir = ref(new PreJobAirBooking());
let editPreJobSea = ref(new PreJobSeaBooking());
let editPreJobRoad = ref(new PreJobRoadBooking());
let editPreJobRail = ref(new PreJobRailBooking());

onMounted(() => {

  if (route.params.id !== 'new') {

    // Must be edit, get the record to display
    getCompanyToEdit();
  }

})

const modeViewModel = computed({

  get() {

    if (route.params.section === 'Air') {

      if (route.params.id === 'new') return preJobAirBooking.value;

      // Clear any validation triggers for new edit data
      validationViewModel.value.preJobAir = new PreJobAirBookingValidation();

      return editPreJobAir.value;
    }

    if (route.params.section === 'Sea') {

      if (route.params.id === 'new') return preJobSeaBooking.value;

      validationViewModel.value.preJobSea = new PreJobSeaBookingValidation();

      return editPreJobSea.value;
    }

    if (route.params.section === 'Road') {

      if (route.params.id === 'new') return preJobRoadBooking.value;

      validationViewModel.value.preJobRoad = new PreJobRoadBookingValidation();

      return editPreJobRoad.value;
    }

    if (route.params.section === 'Rail') {

      if (route.params.id === 'new') return preJobRailBooking.value;

      validationViewModel.value.preJobRail = new PreJobRailBookingValidation();

      return editPreJobRail.value;
    }

  },

  set(value) {

    // STACK

    //updated from handleNewBooking function in MultiPreJob Line 402

    const newBookingType = value.constructor.name;

    switch (newBookingType) {
      case('PreJobAirBooking') : preJobAirBooking.value = value;
        break;
      case('PreJobSeaBooking') : preJobSeaBooking.value = value;
        break;
      case('PreJobRoadBooking') : preJobRoadBooking.value = value;
        break;
      case('PreJobRailBooking') :  preJobRailBooking.value = value;
        break;
    }

  }

});

const modeValidationViewModel = computed(() => {

  if (route.params.section === 'Air') return 'preJobAir';
  if (route.params.section === 'Sea') return 'preJobSea';
  if (route.params.section === 'Road') return 'preJobRoad';
  if (route.params.section === 'Rail') return 'preJobRail';

})

const tileSectionColor = computed(() => {

  if (route.params.section === 'Air') return '#525777';
  if (route.params.section === 'Sea') return '#5b4255';
  if (route.params.section === 'Road') return '#35696a';
  if (route.params.section === 'Rail') return '#4c2e2e';

})

const route = useRoute();
const router = useRouter();

async function getCompanyToEdit() {

  const companyDetail = await API.getCompanyToEdit(route, 'freightBooking', route.params.section) ?? null;

  if(!companyDetail.isEditable) {
    await router.push({name: 'jobNotEditableUserInfo'});
    return;
  }

  switch (route.params.section) {

    case 'Air' : mapCompanyToEdit(editPreJobAir, 'flightShipment', companyDetail);
      break;
    case 'Sea' :  mapCompanyToEdit(editPreJobSea, 'seaShipment', companyDetail);
      break;
    case 'Road' :  mapCompanyToEdit(editPreJobRoad, 'roadShipment', companyDetail);
      break;
    case 'Rail' :  mapCompanyToEdit(editPreJobRail, 'railShipment', companyDetail);
      break;
  }

}

function mapCompanyToEdit(model, section, companyDetail) {

  model.value = {...model.value, ...companyDetail};

  model.value.isUpdate.shipper = true;
  model.value.isUpdate.consignee = true;

  model.value.shipper.shipperRef = companyDetail[section].shipperRef;
  model.value.consignee.consigneeRef = companyDetail[section].consigneeRef;
  model.value.consignee.additionalRef = companyDetail[section].consigneeRefAdditional;

  model.value.dimensions.unitOfMeasure === 'imperial' ? model.value.unitOfMeasureSwitch = true :
      model.value.unitOfMeasureSwitch = false;

  delete model.value.dimensions.unitOfMeasure;

  mapContacts(model.value);
}

async function mapContacts(model) {

  const shipper = await API.getCompanyDetail(model.shipper.id, route) ?? null;

  model.shipper.shipperContacts = [];


  if(shipper) {

    shipper.contacts?.forEach(contact => {

      model.shipper.shipperContacts.push(contact)
    });

    let shipperContact = model.shipper.shipperContacts.find(c =>

        c.firstName === model.shipper.shipperContactFirstName &&
        c.lastName === model.shipper.shipperContactLastName &&
        c.title === model.shipper.shipperContactTitle &&
        c.email === model.shipper.shipperContactEmail &&
        c.phone === model.shipper.shipperContactPhone &&
        c.value === model.shipper.shipperContactFullName
    ) ?? null;

    if(!shipperContact) {

      model.shipper.shipperContactFullName = '';

    } else {

      model.shipper.shipperContactId = shipperContact.id;
      model.shipper.shipperContactDepartment = shipperContact.department;
    }

  }

  model.consignee.consigneeContacts = [];

  const consignee = await API.getCompanyDetail(model.consignee.id, route);


  if(consignee) {

    consignee.contacts?.forEach(contact => {

      model.consignee.consigneeContacts.push(contact)
    });

    let consigneeContact = model.consignee.consigneeContacts.find(c =>

        c.firstName === model.consignee.consigneeContactFirstName &&
        c.lastName === model.consignee.consigneeContactLastName &&
        c.title === model.consignee.consigneeContactTitle &&
        c.email === model.consignee.consigneeContactEmail &&
        c.phone === model.consignee.consigneeContactPhone &&
        c.value === model.consignee.consigneeContactFullName
    ) ?? null;

    if(!consigneeContact) {

      model.consignee.consigneeContactFullName = '';

    } else {

      model.consignee.consigneeContactId = consigneeContact.id;
      model.consignee.consigneeContactDepartment = consigneeContact.department;
    }

  }

}

</script>

<template>
  <MultiPreJob
      :key="modeViewModel"
      :tileColor="tileSectionColor"
      :mode="route.params.section"
      :validationViewModelSection="modeValidationViewModel"
      v-model="modeViewModel">
  </MultiPreJob>
</template>


